/**
 * RTK Query endpoints cho vận hành / quản trị (`/api/*`, `/metrics`, …).
 * Response được chuẩn hóa qua `unwrapOpsList` / `unwrapOpsObject` (hỗ trợ envelope `{ data }`).
 */
import type { Infrastructure } from '@/features/map/types/map.types';
import { mapInfrastructureApiRow } from '@/features/map/utils/mapInfrastructureFromApi';
import type { MenuAdminCreateBody } from '@/features/menus/types/menu.types';
import type { AuditLogEntry } from '@/mocks/auditLogs';
import { baseApi } from '@/stores/api';

import type {
  AdminUserActiveBody,
  AdminUserCreateBody,
  AdminUserPasswordBody,
  AdminUserRoleBody,
  AdminUsersListParams,
  CatalogResource,
  CreateNetcdfJobBody,
  MapLayersPublishedParams,
  MenuRoleDto,
  NetcdfJobsListParams,
  OpsListLimit,
  PresignArtifactBody,
  StationWaterLevelsParams,
  StationsListParams,
} from '../types/ops.types';
import type {
  AlertAckBody,
  AlertEventRow,
  AlertHistoryQueryParams,
  AlertThresholdsQueryParams,
  DashboardSummaryDto,
  IotExportQueryParams,
  OpsPaginated,
  PutStationThresholdItem,
  PutStationThresholdsBulkResult,
  SensorTypeCreateBody,
  SensorTypeDto,
  SensorTypePatchBody,
  SensorTypesListParams,
  SettingsBulkBody,
  StationSensorAlertRuleRow,
  StationLinkProtocolItemDto,
  StationsAggregateParams,
} from '../types/opsExtended.types';
import type {
  StationTelemetryQueryParams,
  StationTelemetrySeriesDto,
} from '../types/stationTelemetry.types';
import type { AdminUserRow } from '../utils/adminUserRow';
import { mapAdminUserDtoToRow } from '../utils/mapAdminUserDto';
import { mapAuditLogDtoToEntry } from '../utils/mapAuditLogDto';
import { normalizeStationTelemetryPoints } from '../utils/normalizeStationTelemetryPoints';
import { unwrapOpsList, unwrapOpsObject, unwrapOpsPaginated } from '../utils/opsResponse';

function adminUserListTags(rows: AdminUserRow[]) {
  return [
    ...rows.map((r) => ({ type: 'AdminUser' as const, id: r.id })),
    { type: 'AdminUser' as const, id: 'LIST' },
  ];
}

/**
 * API quản trị / vận hành (prefix `/api/...`).
 * Response shape: ưu tiên envelope `{ data }`; danh sách có thể là mảng hoặc `{ items }` — xem `unwrapOpsList`.
 */
export const opsManagementApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getServiceRoot: builder.query<Record<string, unknown>, void>({
      query: () => ({ url: '/' }),
      transformResponse: (raw: unknown) => unwrapOpsObject(raw) ?? {},
    }),
    /** `/metrics` — OpenAPI ghi `application/json` + schema string; thực tế thường là text Prometheus → dùng `text`. */
    getMetricsText: builder.query<string, void>({
      query: () => ({ url: '/metrics', responseType: 'text' }),
      transformResponse: (raw: unknown) => (typeof raw === 'string' ? raw : String(raw ?? '')),
    }),

    /** GET /api/reports/dashboard-summary — KPI điều hành. */
    getDashboardSummary: builder.query<DashboardSummaryDto, void>({
      query: () => ({ url: '/api/reports/dashboard-summary' }),
      transformResponse: (raw: unknown) =>
        (unwrapOpsObject<DashboardSummaryDto>(raw) as DashboardSummaryDto | null) ?? {
          generated_at: new Date().toISOString(),
          stations: { total: 0, active_last_24h: 0, with_open_alerts: 0 },
          alerts: { open: 0, acknowledged: 0, resolved_7d: 0 },
          flood: { analysis_jobs_total: 0, analysis_jobs_running: 0 },
          trends: { water_level_7d: [] },
        },
      providesTags: [{ type: 'ReportSummary', id: 'DASHBOARD' }],
    }),

    /** GET /api/reports/iot-export — CSV (text). */
    getIotExportCsv: builder.query<string, IotExportQueryParams>({
      query: (params) => ({
        url: '/api/reports/iot-export',
        params,
        responseType: 'text',
      }),
      transformResponse: (raw: unknown) => (typeof raw === 'string' ? raw : String(raw ?? '')),
    }),

    /** GET /api/settings — map key → value. */
    getSettingsMap: builder.query<Record<string, string>, void>({
      query: () => ({ url: '/api/settings' }),
      transformResponse: (raw: unknown) => {
        const o = unwrapOpsObject<Record<string, string>>(raw);
        return o && typeof o === 'object' ? o : {};
      },
      providesTags: [{ type: 'OpsSetting', id: 'MAP' }],
    }),

    getSettingByKey: builder.query<{ key: string; value: string | null }, string>({
      query: (key) => ({ url: `/api/settings/${encodeURIComponent(key)}` }),
      transformResponse: (raw: unknown) => {
        const o = unwrapOpsObject<{ key: string; value: string | null }>(raw);
        return o ?? { key: '', value: null };
      },
      providesTags: (_r, _e, key) => [{ type: 'OpsSetting', id: key }],
    }),

    putSetting: builder.mutation<
      { ok: boolean; key: string; value: string },
      { key: string; value: string }
    >({
      query: ({ key, value }) => ({
        url: `/api/settings/${encodeURIComponent(key)}`,
        method: 'PUT',
        data: { value },
      }),
      transformResponse: (raw: unknown): { ok: boolean; key: string; value: string } => {
        const o = unwrapOpsObject(raw) as { ok?: boolean; key?: string; value?: string } | null;
        if (o && typeof o === 'object') {
          return {
            ok: o.ok ?? true,
            key: o.key ?? '',
            value: o.value ?? '',
          };
        }
        return { ok: true, key: '', value: '' };
      },
      invalidatesTags: (_r, _e, { key }) => [
        { type: 'OpsSetting', id: 'MAP' },
        { type: 'OpsSetting', id: key },
      ],
    }),

    postSettingsBulk: builder.mutation<
      { ok: boolean; updated: number; keys: string[] },
      SettingsBulkBody
    >({
      query: (body) => ({ url: '/api/settings/bulk', method: 'POST', data: body }),
      transformResponse: (raw: unknown): { ok: boolean; updated: number; keys: string[] } => {
        const o = unwrapOpsObject(raw) as {
          ok?: boolean;
          updated?: number;
          keys?: string[];
        } | null;
        if (o && typeof o === 'object') {
          return {
            ok: o.ok ?? true,
            updated: o.updated ?? 0,
            keys: Array.isArray(o.keys) ? o.keys : [],
          };
        }
        return { ok: true, updated: 0, keys: [] };
      },
      invalidatesTags: (_r, _m, body) => [
        { type: 'OpsSetting', id: 'MAP' },
        ...Object.keys(body.changes ?? {}).map((k) => ({ type: 'OpsSetting' as const, id: k })),
      ],
    }),

    /** GET `/api/sensor-types` — phân trang; FE lấy trang 1, pageSize tối đa 200 cho dropdown/layer. */
    getSensorTypes: builder.query<SensorTypeDto[], void | SensorTypesListParams>({
      query: (arg) => {
        const p: SensorTypesListParams =
          arg && typeof arg === 'object' ? arg : { page: 1, pageSize: 200 };
        return {
          url: '/api/sensor-types',
          params: {
            page: p.page ?? 1,
            pageSize: Math.min(p.pageSize ?? 200, 200),
            ...(p.q !== undefined && p.q !== '' ? { q: p.q } : {}),
            ...(p.group_code ? { group_code: p.group_code } : {}),
          },
        };
      },
      transformResponse: (raw: unknown) => unwrapOpsPaginated<SensorTypeDto>(raw).items,
      providesTags: [{ type: 'OpsAlert', id: 'SENSOR-TYPES' }],
    }),

    /** GET `/api/sensor-types` — trả đủ `items` + `total` + `page` + `pageSize` cho bảng vận hành. */
    getSensorTypesPaginated: builder.query<
      OpsPaginated<SensorTypeDto>,
      SensorTypesListParams | void
    >({
      query: (arg) => {
        const p: SensorTypesListParams =
          arg && typeof arg === 'object' ? arg : { page: 1, pageSize: 20 };
        return {
          url: '/api/sensor-types',
          params: {
            page: p.page ?? 1,
            pageSize: Math.min(p.pageSize ?? 20, 200),
            ...(p.q !== undefined && p.q !== '' ? { q: p.q } : {}),
            ...(p.group_code ? { group_code: p.group_code } : {}),
          },
        };
      },
      transformResponse: (raw: unknown) => unwrapOpsPaginated<SensorTypeDto>(raw),
      providesTags: [{ type: 'OpsAlert', id: 'SENSOR-TYPES' }],
    }),

    createSensorType: builder.mutation<SensorTypeDto, SensorTypeCreateBody>({
      query: (body) => ({
        url: '/api/sensor-types',
        method: 'POST',
        data: body,
      }),
      transformResponse: (raw: unknown) =>
        (unwrapOpsObject<SensorTypeDto>(raw) as SensorTypeDto | null) ?? {
          code: '',
          group_code: '',
          name: '',
          default_unit: null,
          supports_window: false,
        },
      invalidatesTags: [{ type: 'OpsAlert', id: 'SENSOR-TYPES' }],
    }),

    patchSensorType: builder.mutation<SensorTypeDto, { code: string; body: SensorTypePatchBody }>({
      query: ({ code, body }) => ({
        url: `/api/sensor-types/${encodeURIComponent(code)}`,
        method: 'PATCH',
        data: body,
      }),
      transformResponse: (raw: unknown) =>
        (unwrapOpsObject<SensorTypeDto>(raw) as SensorTypeDto | null) ?? {
          code: '',
          group_code: '',
          name: '',
          default_unit: null,
          supports_window: false,
        },
      invalidatesTags: [{ type: 'OpsAlert', id: 'SENSOR-TYPES' }],
    }),

    deleteSensorType: builder.mutation<{ deleted: boolean; code: string }, string>({
      query: (code) => ({
        url: `/api/sensor-types/${encodeURIComponent(code)}`,
        method: 'DELETE',
      }),
      transformResponse: (raw: unknown) => {
        const o = unwrapOpsObject<{ deleted?: boolean; code?: string }>(raw);
        return {
          deleted: o?.deleted ?? true,
          code: o?.code ?? '',
        };
      },
      invalidatesTags: [{ type: 'OpsAlert', id: 'SENSOR-TYPES' }],
    }),

    getAlertThresholds: builder.query<
      OpsPaginated<StationSensorAlertRuleRow>,
      AlertThresholdsQueryParams | void
    >({
      query: (params) => ({
        url: '/api/alerts/thresholds',
        params: params ?? undefined,
      }),
      transformResponse: (raw: unknown) => {
        const paged = unwrapOpsPaginated<StationSensorAlertRuleRow>(raw);
        return {
          ...paged,
          items: paged.items.map((row) => {
            const code = row.channel_code || row.routing_key || '';
            return { ...row, channel_code: code, routing_key: row.routing_key || code };
          }),
        };
      },
      providesTags: [{ type: 'OpsAlert', id: 'THRESHOLDS' }],
    }),

    getAlertThresholdById: builder.query<StationSensorAlertRuleRow, string>({
      query: (id) => ({ url: `/api/alerts/thresholds/${encodeURIComponent(id)}` }),
      transformResponse: (raw: unknown) =>
        (unwrapOpsObject<StationSensorAlertRuleRow>(raw) as StationSensorAlertRuleRow | null) ?? {
          id: '',
          station_id: '',
          channel_code: '',
          window_code: '',
        },
      providesTags: (_r, _e, id) => [{ type: 'OpsAlert', id: `TH-${id}` }],
    }),

    putStationThresholdsBulk: builder.mutation<
      PutStationThresholdsBulkResult,
      { stationId: string; items: PutStationThresholdItem[] }
    >({
      query: ({ stationId, items }) => ({
        url: `/api/alerts/stations/${encodeURIComponent(stationId)}/thresholds`,
        method: 'PUT',
        data: { items },
      }),
      transformResponse: (raw: unknown) => {
        const o = unwrapOpsObject<PutStationThresholdsBulkResult>(raw);
        return (
          o ?? {
            items: [],
            updated_count: 0,
          }
        );
      },
      invalidatesTags: [
        { type: 'OpsAlert', id: 'THRESHOLDS' },
        { type: 'OpsAlert', id: 'HISTORY' },
        { type: 'ReportSummary', id: 'DASHBOARD' },
      ],
    }),

    patchAlertThresholdRule: builder.mutation<
      StationSensorAlertRuleRow,
      { id: string; body: Partial<Omit<PutStationThresholdItem, 'id'>> }
    >({
      query: ({ id, body }) => ({
        url: `/api/alerts/thresholds/${encodeURIComponent(id)}`,
        method: 'PATCH',
        data: body,
      }),
      transformResponse: (raw: unknown) =>
        (unwrapOpsObject<StationSensorAlertRuleRow>(raw) as StationSensorAlertRuleRow | null) ?? {
          id: '',
          station_id: '',
          channel_code: '',
          window_code: '',
        },
      invalidatesTags: [
        { type: 'OpsAlert', id: 'THRESHOLDS' },
        { type: 'OpsAlert', id: 'HISTORY' },
        { type: 'ReportSummary', id: 'DASHBOARD' },
      ],
    }),

    /** Lich su su kien nguong — GET /api/alerts/threshold-events (BE: `pageno` / `pagesize`). */
    getAlertHistory: builder.query<OpsPaginated<AlertEventRow>, AlertHistoryQueryParams | void>({
      query: (params) => {
        if (!params) return { url: '/api/alerts/threshold-events' };
        const { page, pageSize, ...rest } = params;
        return {
          url: '/api/alerts/threshold-events',
          params: {
            ...rest,
            ...(page != null ? { pageno: page } : {}),
            ...(pageSize != null ? { pagesize: pageSize } : {}),
          },
        };
      },
      transformResponse: (raw: unknown) => unwrapOpsPaginated<AlertEventRow>(raw),
      providesTags: [{ type: 'OpsAlert', id: 'HISTORY' }],
    }),

    patchAlertAck: builder.mutation<
      AlertEventRow,
      {
        id: string;
        body: AlertAckBody;
        /** Bulk ACK: invalidate once after all patches. */ silentInvalidate?: boolean;
      }
    >({
      query: ({ id, body }) => ({
        url: `/api/alerts/threshold-events/${encodeURIComponent(id)}/ack`,
        method: 'PATCH',
        data: body,
      }),
      transformResponse: (raw: unknown) =>
        (unwrapOpsObject<AlertEventRow>(raw) as AlertEventRow | null) ?? { id: '' },
      invalidatesTags: (_result, _error, arg) =>
        arg.silentInvalidate
          ? []
          : [
              { type: 'OpsAlert', id: 'HISTORY' },
              { type: 'ReportSummary', id: 'DASHBOARD' },
            ],
    }),

    /** GET /api/stations/aggregate — @Public trên BE; bbox + metric. */
    getStationsAggregate: builder.query<Record<string, unknown>[], StationsAggregateParams>({
      query: (params) => ({
        url: '/api/stations/aggregate',
        params: {
          metric: params.metric,
          minLon: params.minLon,
          minLat: params.minLat,
          maxLon: params.maxLon,
          maxLat: params.maxLat,
          from: params.from,
          to: params.to,
          ...(params.page != null ? { page: params.page } : {}),
          ...(params.pageSize != null ? { pageSize: params.pageSize } : {}),
        },
      }),
      transformResponse: (raw: unknown) => unwrapOpsList<Record<string, unknown>>(raw),
      providesTags: [{ type: 'StationAggregate', id: 'LIST' }],
    }),

    /**
     * GET /api/audit-logs — nhật ký kiểm tra; DTO có thể khác tên field, map qua {@link mapAuditLogDtoToEntry}.
     */
    getAuditLogs: builder.query<AuditLogEntry[], void>({
      query: () => ({ url: '/api/audit-logs' }),
      transformResponse: (raw: unknown) =>
        unwrapOpsList<Record<string, unknown>>(raw).map(mapAuditLogDtoToEntry),
      providesTags: [{ type: 'AuditLog', id: 'LIST' }],
    }),

    getAdminUsers: builder.query<
      { items: AdminUserRow[]; total: number; page: number; pageSize: number },
      AdminUsersListParams | void
    >({
      query: (args) => {
        const p = args ?? {};
        const params: Record<string, string | number> = {
          page: p.page ?? 1,
          pageSize: p.pageSize ?? 20,
        };
        const q = p.q?.trim();
        if (q) params.q = q;
        const role = p.role?.trim();
        if (role) params.role = role;
        const appendUuid = (k: keyof AdminUsersListParams) => {
          const v = typeof p[k] === 'string' ? (p[k] as string).trim() : '';
          if (v) params[k] = v;
        };
        appendUuid('department_id');
        appendUuid('academic_rank_id');
        appendUuid('academic_degree_id');
        appendUuid('position_id');
        appendUuid('professional_title_id');
        appendUuid('employment_type_id');
        return { url: '/api/users', params };
      },
      transformResponse: (raw: unknown) => {
        const p = unwrapOpsPaginated<Record<string, unknown>>(raw);
        return {
          items: p.items.map(mapAdminUserDtoToRow),
          total: p.total,
          page: p.page,
          pageSize: p.pageSize,
        };
      },
      providesTags: (result) =>
        result?.items?.length
          ? adminUserListTags(result.items)
          : [{ type: 'AdminUser', id: 'LIST' }],
    }),
    getAdminUserById: builder.query<Record<string, unknown>, string>({
      query: (id) => ({ url: `/api/users/${id}` }),
      transformResponse: (raw: unknown) => unwrapOpsObject(raw) ?? {},
      providesTags: (_r, _e, id) => [{ type: 'AdminUser', id }],
    }),
    createAdminUser: builder.mutation<Record<string, unknown>, AdminUserCreateBody>({
      query: (body) => ({ url: '/api/users', method: 'POST', data: body }),
      transformResponse: (raw: unknown) => unwrapOpsObject(raw) ?? {},
      invalidatesTags: [{ type: 'AdminUser', id: 'LIST' }],
    }),
    /** PATCH /api/users/{id} — multipart/form-data (OpenAPI). */
    patchAdminUser: builder.mutation<Record<string, unknown>, { id: string; formData: FormData }>({
      query: ({ id, formData }) => ({
        url: `/api/users/${id}`,
        method: 'PATCH',
        data: formData,
      }),
      transformResponse: (raw: unknown) => unwrapOpsObject(raw) ?? {},
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'AdminUser', id: 'LIST' },
        { type: 'AdminUser', id },
      ],
    }),
    deleteAdminUser: builder.mutation<void, string>({
      query: (id) => ({ url: `/api/users/${id}`, method: 'DELETE' }),
      transformResponse: () => undefined,
      invalidatesTags: (_r, _e, id) => [
        { type: 'AdminUser', id: 'LIST' },
        { type: 'AdminUser', id },
      ],
    }),
    putAdminUserPassword: builder.mutation<void, { id: string; body: AdminUserPasswordBody }>({
      query: ({ id, body }) => ({
        url: `/api/users/${id}/password`,
        method: 'PUT',
        data: body,
      }),
      transformResponse: () => undefined,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'AdminUser', id }],
    }),
    putAdminUserRole: builder.mutation<void, { id: string; body: AdminUserRoleBody }>({
      query: ({ id, body }) => ({
        url: `/api/users/${id}/role`,
        method: 'PUT',
        data: body,
      }),
      transformResponse: () => undefined,
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'AdminUser', id: 'LIST' },
        { type: 'AdminUser', id },
      ],
    }),
    putAdminUserActive: builder.mutation<void, { id: string; body: AdminUserActiveBody }>({
      query: ({ id, body }) => ({
        url: `/api/users/${id}/active`,
        method: 'PUT',
        data: body,
      }),
      transformResponse: () => undefined,
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'AdminUser', id: 'LIST' },
        { type: 'AdminUser', id },
      ],
    }),
    getUsersImportTemplate: builder.query<Blob, void>({
      query: () => ({
        url: '/api/users/import/template',
        responseType: 'blob',
      }),
    }),
    importUsersExcel: builder.mutation<Record<string, unknown>, FormData>({
      query: (formData) => ({
        url: '/api/users/import',
        method: 'POST',
        data: formData,
      }),
      transformResponse: (raw: unknown) => unwrapOpsObject(raw) ?? {},
      invalidatesTags: [{ type: 'AdminUser', id: 'LIST' }],
    }),

    getMenusAdminTree: builder.query<Record<string, unknown>[], void>({
      query: () => ({
        url: '/api/menus',
        params: { include_inactive: 'true' },
      }),
      transformResponse: (raw: unknown) => unwrapOpsList<Record<string, unknown>>(raw),
      providesTags: [{ type: 'AdminMenu', id: 'LIST' }],
    }),
    /** GET /api/roles — danh sách role; BE có thể trả `code` hoặc `slug`. */
    getMenuRoles: builder.query<MenuRoleDto[], void>({
      query: () => ({ url: '/api/roles' }),
      transformResponse: (raw: unknown) => {
        const list = unwrapOpsList<Record<string, unknown>>(raw);
        return list
          .map((x) => {
            const id =
              typeof x.code === 'string'
                ? x.code
                : typeof x.slug === 'string'
                  ? x.slug
                  : typeof x.role_slug === 'string'
                    ? x.role_slug
                    : '';
            if (!id) return null;
            return {
              slug: id,
              name: typeof x.name === 'string' ? x.name : id,
              description: typeof x.description === 'string' ? x.description : undefined,
            } as MenuRoleDto;
          })
          .filter((x): x is MenuRoleDto => x !== null);
      },
      providesTags: [{ type: 'AdminMenu', id: 'ROLES' }],
    }),
    getMenuById: builder.query<Record<string, unknown>, string>({
      query: (id) => ({ url: `/api/menus/${id}` }),
      transformResponse: (raw: unknown) => unwrapOpsObject(raw) ?? {},
      providesTags: (_r, _e, id) => [{ type: 'AdminMenu', id }],
    }),
    getMenuRolesForMenu: builder.query<string[], string>({
      query: (id) => ({ url: `/api/menus/${id}/roles` }),
      transformResponse: (raw: unknown) => {
        const inner = unwrapOpsObject<{ role_slugs?: string[] }>(raw);
        if (inner?.role_slugs) return inner.role_slugs;
        return unwrapOpsList(raw) as string[];
      },
      providesTags: (_r, _e, id) => [{ type: 'AdminMenu', id: `${id}-roles` }],
    }),
    createMenu: builder.mutation<Record<string, unknown>, MenuAdminCreateBody>({
      query: (body) => ({ url: '/api/menus', method: 'POST', data: body }),
      transformResponse: (raw: unknown) => unwrapOpsObject(raw) ?? {},
      invalidatesTags: [{ type: 'AdminMenu', id: 'LIST' }, 'Menu'],
    }),
    patchMenu: builder.mutation<
      Record<string, unknown>,
      { id: string; body: Record<string, unknown> }
    >({
      query: ({ id, body }) => ({ url: `/api/menus/${id}`, method: 'PATCH', data: body }),
      transformResponse: (raw: unknown) => unwrapOpsObject(raw) ?? {},
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'AdminMenu', id: 'LIST' },
        { type: 'AdminMenu', id },
        'Menu',
      ],
    }),
    deleteMenu: builder.mutation<void, string>({
      query: (id) => ({ url: `/api/menus/${id}`, method: 'DELETE' }),
      transformResponse: () => undefined,
      invalidatesTags: [{ type: 'AdminMenu', id: 'LIST' }, 'Menu'],
    }),
    putMenuRoles: builder.mutation<void, { id: string; roleSlugs: string[] }>({
      query: ({ id, roleSlugs }) => ({
        url: `/api/menus/${id}/roles`,
        method: 'PUT',
        data: { role_slugs: roleSlugs },
      }),
      transformResponse: () => undefined,
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'AdminMenu', id: 'LIST' },
        { type: 'AdminMenu', id },
        { type: 'AdminMenu', id: `${id}-roles` },
        'Menu',
      ],
    }),

    getMapLayersPublished: builder.query<
      Record<string, unknown>[],
      MapLayersPublishedParams | void
    >({
      query: (arg) => ({
        url: '/api/map-layers/published',
        params: arg && typeof arg === 'object' && arg.group ? { group: arg.group } : undefined,
      }),
      transformResponse: (raw: unknown) => unwrapOpsList<Record<string, unknown>>(raw),
      providesTags: [{ type: 'MapLayer', id: 'PUBLISHED' }],
    }),
    getMapLayers: builder.query<Record<string, unknown>[], OpsListLimit | void>({
      query: (arg) => ({
        url: '/api/map-layers',
        params: arg && typeof arg === 'object' && arg.limit ? { limit: arg.limit } : undefined,
      }),
      transformResponse: (raw: unknown) => unwrapOpsList<Record<string, unknown>>(raw),
      providesTags: [{ type: 'MapLayer', id: 'LIST' }],
    }),
    createMapLayer: builder.mutation<Record<string, unknown>, Record<string, unknown>>({
      query: (body) => ({ url: '/api/map-layers', method: 'POST', data: body }),
      transformResponse: (raw: unknown) => unwrapOpsObject(raw) ?? {},
      invalidatesTags: [
        { type: 'MapLayer', id: 'LIST' },
        { type: 'MapLayer', id: 'PUBLISHED' },
      ],
    }),
    patchMapLayerPublish: builder.mutation<
      Record<string, unknown>,
      { id: string; body: Record<string, unknown> }
    >({
      query: ({ id, body }) => ({
        url: `/api/map-layers/${id}/publish`,
        method: 'PATCH',
        data: body,
      }),
      transformResponse: (raw: unknown) => unwrapOpsObject(raw) ?? {},
      invalidatesTags: [
        { type: 'MapLayer', id: 'LIST' },
        { type: 'MapLayer', id: 'PUBLISHED' },
      ],
    }),
    patchMapLayerStyle: builder.mutation<
      Record<string, unknown>,
      { id: string; body: Record<string, unknown> }
    >({
      query: ({ id, body }) => ({
        url: `/api/map-layers/${id}/style`,
        method: 'PATCH',
        data: body,
      }),
      transformResponse: (raw: unknown) => unwrapOpsObject(raw) ?? {},
      invalidatesTags: [
        { type: 'MapLayer', id: 'LIST' },
        { type: 'MapLayer', id: 'PUBLISHED' },
      ],
    }),
    patchMapLayerAcl: builder.mutation<
      Record<string, unknown>,
      { id: string; body: Record<string, unknown> }
    >({
      query: ({ id, body }) => ({
        url: `/api/map-layers/${id}/acl`,
        method: 'PATCH',
        data: body,
      }),
      transformResponse: (raw: unknown) => unwrapOpsObject(raw) ?? {},
      invalidatesTags: [
        { type: 'MapLayer', id: 'LIST' },
        { type: 'MapLayer', id: 'PUBLISHED' },
      ],
    }),

    /** GET /api/map/infrastructure — hạ tầng GeoJSON trong bbox (gateway). */
    getMapInfrastructure: builder.query<Infrastructure[], { bbox?: string; type?: string }>({
      query: (params) => ({
        url: '/api/map/infrastructure',
        params: {
          ...(params.bbox ? { bbox: params.bbox } : {}),
          ...(params.type ? { type: params.type } : {}),
        },
      }),
      transformResponse: (raw: unknown) => {
        const list = unwrapOpsList<Record<string, unknown>>(raw);
        return list
          .map((row) => mapInfrastructureApiRow(row))
          .filter((x): x is Infrastructure => x != null);
      },
      providesTags: [{ type: 'Map', id: 'INFRASTRUCTURE' }],
    }),

    /** GET /api/provinces — danh sách tỉnh/thành (paginated). */
    getProvincesList: builder.query<
      Array<{ code: string; name: string }>,
      { page?: number; pageSize?: number; q?: string } | void
    >({
      query: (params) => ({
        url: '/api/provinces',
        params: {
          page: params?.page ?? 1,
          pageSize: params?.pageSize ?? 200,
          ...(params?.q ? { q: params.q } : {}),
        },
      }),
      transformResponse: (raw: unknown) =>
        unwrapOpsList<{ code?: string; name?: string }>(raw)
          .map((r) => ({ code: String(r.code ?? '').trim(), name: String(r.name ?? '').trim() }))
          .filter((r) => r.code.length > 0),
      providesTags: [{ type: 'Catalog', id: 'PROVINCES' }],
    }),

    /** GET /api/wards — phường/xã, lọc theo provinceCode. */
    getWardsList: builder.query<
      Array<{ code: string; name: string; province_code: string; province_name: string }>,
      { provinceCode?: string; page?: number; pageSize?: number; q?: string } | void
    >({
      query: (params) => ({
        url: '/api/wards',
        params: {
          page: params?.page ?? 1,
          pageSize: params?.pageSize ?? 500,
          ...(params?.provinceCode ? { provinceCode: params.provinceCode } : {}),
          ...(params?.q ? { q: params.q } : {}),
        },
      }),
      transformResponse: (raw: unknown) =>
        unwrapOpsList<{
          code?: string;
          name?: string;
          province_code?: string;
          province_name?: string;
        }>(raw)
          .map((r) => ({
            code: String(r.code ?? '').trim(),
            name: String(r.name ?? '').trim(),
            province_code: String(r.province_code ?? '').trim(),
            province_name: String(r.province_name ?? '').trim(),
          }))
          .filter((r) => r.code.length > 0),
      providesTags: (_r, _e, arg) => [
        { type: 'Catalog', id: `WARDS:${arg?.provinceCode ?? 'ALL'}` },
      ],
    }),

    /** GET /api/basins — danh mục lưu vực. */
    getBasinsList: builder.query<
      Array<{ code: string; name: string }>,
      { page?: number; pageSize?: number; q?: string } | void
    >({
      query: (params) => ({
        url: '/api/basins',
        params: {
          page: params?.page ?? 1,
          pageSize: params?.pageSize ?? 200,
          ...(params?.q ? { q: params.q } : {}),
        },
      }),
      transformResponse: (raw: unknown) =>
        unwrapOpsList<{ code?: string; name?: string }>(raw)
          .map((r) => ({ code: String(r.code ?? '').trim(), name: String(r.name ?? '').trim() }))
          .filter((r) => r.code.length > 0),
      providesTags: [{ type: 'Basin', id: 'CATALOG' }],
    }),

    getCatalogRoot: builder.query<Record<string, unknown>, void>({
      query: () => ({ url: '/api/catalog' }),
      transformResponse: (raw: unknown) => unwrapOpsObject(raw) ?? {},
      providesTags: [{ type: 'Catalog', id: 'ROOT' }],
    }),
    getCatalogRows: builder.query<Record<string, unknown>[], CatalogResource>({
      query: (resource) => ({ url: `/api/catalog/${resource}` }),
      transformResponse: (raw: unknown) => unwrapOpsList<Record<string, unknown>>(raw),
      providesTags: (_r, _e, resource) => [{ type: 'Catalog', id: resource }],
    }),
    getCatalogRow: builder.query<
      Record<string, unknown>,
      { resource: CatalogResource; id: string }
    >({
      query: ({ resource, id }) => ({
        url: `/api/catalog/${resource}/${encodeURIComponent(id)}`,
      }),
      transformResponse: (raw: unknown) => unwrapOpsObject(raw) ?? {},
      providesTags: (_r, _e, { resource, id }) => [
        { type: 'Catalog', id: resource },
        { type: 'Catalog', id: `${resource}:${id}` },
      ],
    }),
    createCatalogRow: builder.mutation<
      Record<string, unknown>,
      { resource: CatalogResource; body: Record<string, unknown> }
    >({
      query: ({ resource, body }) => ({
        url: `/api/catalog/${resource}`,
        method: 'POST',
        data: body,
      }),
      transformResponse: (raw: unknown) => unwrapOpsObject(raw) ?? {},
      invalidatesTags: (_r, _e, { resource }) => [
        { type: 'Catalog', id: resource },
        { type: 'Catalog', id: 'ROOT' },
      ],
    }),
    putCatalogRow: builder.mutation<
      Record<string, unknown>,
      { resource: CatalogResource; id: string; body: Record<string, unknown> }
    >({
      query: ({ resource, id, body }) => ({
        url: `/api/catalog/${resource}/${encodeURIComponent(id)}`,
        method: 'PUT',
        data: body,
      }),
      transformResponse: (raw: unknown) => unwrapOpsObject(raw) ?? {},
      invalidatesTags: (_r, _e, { resource, id }) => [
        { type: 'Catalog', id: resource },
        { type: 'Catalog', id: `${resource}:${id}` },
      ],
    }),
    deleteCatalogRow: builder.mutation<void, { resource: CatalogResource; id: string }>({
      query: ({ resource, id }) => ({
        url: `/api/catalog/${resource}/${encodeURIComponent(id)}`,
        method: 'DELETE',
      }),
      transformResponse: () => undefined,
      invalidatesTags: (_r, _e, { resource }) => [
        { type: 'Catalog', id: resource },
        { type: 'Catalog', id: 'ROOT' },
      ],
    }),

    getBasins: builder.query<Record<string, unknown>[], { pageSize?: number } | void>({
      query: (arg) => ({
        url: '/api/basins',
        params: arg?.pageSize ? { pageSize: arg.pageSize } : undefined,
      }),
      transformResponse: (raw: unknown) => unwrapOpsList<Record<string, unknown>>(raw),
      providesTags: [{ type: 'Basin', id: 'LIST' }],
    }),
    getBasinById: builder.query<Record<string, unknown>, string>({
      query: (id) => ({ url: `/api/basins/${encodeURIComponent(id)}` }),
      transformResponse: (raw: unknown) => unwrapOpsObject(raw) ?? {},
      providesTags: (_r, _e, id) => [{ type: 'Basin', id }],
    }),
    createBasin: builder.mutation<Record<string, unknown>, Record<string, unknown>>({
      query: (body) => ({ url: '/api/basins', method: 'POST', data: body }),
      transformResponse: (raw: unknown) => unwrapOpsObject(raw) ?? {},
      invalidatesTags: [{ type: 'Basin', id: 'LIST' }],
    }),
    patchBasin: builder.mutation<
      Record<string, unknown>,
      { id: string; body: Record<string, unknown> }
    >({
      query: ({ id, body }) => ({
        url: `/api/basins/${encodeURIComponent(id)}`,
        method: 'PATCH',
        data: body,
      }),
      transformResponse: (raw: unknown) => unwrapOpsObject(raw) ?? {},
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Basin', id: 'LIST' },
        { type: 'Basin', id },
      ],
    }),
    deleteBasin: builder.mutation<void, string>({
      query: (id) => ({
        url: `/api/basins/${encodeURIComponent(id)}`,
        method: 'DELETE',
      }),
      transformResponse: () => undefined,
      invalidatesTags: [{ type: 'Basin', id: 'LIST' }],
    }),

    getStations: builder.query<Record<string, unknown>[], StationsListParams | void>({
      query: (arg) => {
        const params: Record<string, string | number> = {};
        if (arg?.basinId) params.basinId = arg.basinId;
        if (arg?.page) params.page = arg.page;
        if (arg?.pageSize) params.pageSize = arg.pageSize;
        if (arg?.q) params.q = arg.q;
        return {
          url: '/api/stations',
          params,
        };
      },
      transformResponse: (raw: unknown) => unwrapOpsList<Record<string, unknown>>(raw),
      providesTags: [{ type: 'Station', id: 'LIST' }],
    }),

    /**
     * GET /api/stations/link-protocols — danh mục giao thức cho cấu hình kết nối (`metadata.link.protocol`).
     * Phần tử: `code` (bắt buộc), `name`, `default_port` | `defaultPort`.
     */
    getStationLinkProtocols: builder.query<StationLinkProtocolItemDto[], void>({
      query: () => ({ url: '/api/stations/link-protocols' }),
      transformResponse: (raw: unknown) => {
        const rows = unwrapOpsList<Record<string, unknown>>(raw);
        const out: StationLinkProtocolItemDto[] = [];
        for (const r of rows) {
          const code = String(r.code ?? r.id ?? r.slug ?? '')
            .trim()
            .toLowerCase();
          if (!code) continue;
          const nameRaw =
            typeof r.name === 'string' ? r.name : typeof r.label === 'string' ? r.label : undefined;
          const name = nameRaw?.trim() || undefined;
          const dp = Number(r.default_port ?? r.defaultPort);
          out.push({
            code,
            name,
            defaultPort: Number.isFinite(dp) && dp > 0 && dp <= 65535 ? dp : undefined,
          });
        }
        return out;
      },
      providesTags: [{ type: 'Station', id: 'LINK-PROTOCOLS' }],
    }),
    getStationById: builder.query<Record<string, unknown>, string>({
      query: (id) => ({ url: `/api/stations/${encodeURIComponent(id)}` }),
      transformResponse: (raw: unknown) => unwrapOpsObject(raw) ?? {},
      providesTags: (_r, _e, id) => [{ type: 'Station', id }],
    }),
    getStationWaterLevels: builder.query<
      Record<string, unknown>[],
      { id: string; params?: StationWaterLevelsParams }
    >({
      query: ({ id, params }) => ({
        url: `/api/stations/${encodeURIComponent(id)}/water-levels`,
        params,
      }),
      transformResponse: (raw: unknown) => unwrapOpsList<Record<string, unknown>>(raw),
      providesTags: (_r, _e, { id }) => [{ type: 'Station', id: `${id}-levels` }],
    }),

    /**
     * Unified station time series: tries `GET /api/stations/:id/telemetry`, then `water-levels`.
     * See {@link StationTelemetryQueryParams} / `stationTelemetry.types.ts`.
     */
    getStationTelemetrySeries: builder.query<
      StationTelemetrySeriesDto,
      StationTelemetryQueryParams
    >({
      async queryFn(arg, _api, _extraOptions, baseQuery) {
        const { stationId, from, to, metric } = arg;
        const wlParams = { from, to };

        const tRes = await baseQuery({
          url: `/api/stations/${encodeURIComponent(stationId)}/telemetry`,
          params: { ...wlParams, ...(metric ? { metric } : {}) },
        });
        const tErr = 'error' in tRes ? tRes.error : undefined;
        if (!tErr) {
          const pts = normalizeStationTelemetryPoints(tRes.data);
          if (pts.length > 0) {
            return { data: { points: pts, source: 'telemetry' } };
          }
        }

        const wRes = await baseQuery({
          url: `/api/stations/${encodeURIComponent(stationId)}/water-levels`,
          params: wlParams,
        });
        const wErr = 'error' in wRes ? wRes.error : undefined;
        if (!wErr) {
          const pts = normalizeStationTelemetryPoints(wRes.data);
          if (pts.length > 0) {
            return { data: { points: pts, source: 'water_levels' } };
          }
        }

        if (tErr && wErr) {
          return { error: tErr };
        }
        return { data: { points: [], source: 'telemetry' } };
      },
      providesTags: (_r, _e, arg) => [{ type: 'Station', id: `${arg.stationId}-telemetry` }],
    }),
    createStation: builder.mutation<Record<string, unknown>, Record<string, unknown>>({
      query: (body) => ({ url: '/api/stations', method: 'POST', data: body }),
      transformResponse: (raw: unknown) => unwrapOpsObject(raw) ?? {},
      invalidatesTags: [
        { type: 'Station', id: 'LIST' },
        { type: 'OpsAlert', id: 'THRESHOLDS' },
      ],
    }),
    patchStation: builder.mutation<
      Record<string, unknown>,
      { id: string; body: Record<string, unknown> }
    >({
      query: ({ id, body }) => ({
        url: `/api/stations/${encodeURIComponent(id)}`,
        method: 'PATCH',
        data: body,
      }),
      transformResponse: (raw: unknown) => unwrapOpsObject(raw) ?? {},
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Station', id: 'LIST' },
        { type: 'Station', id },
        { type: 'OpsAlert', id: 'THRESHOLDS' },
      ],
    }),
    deleteStation: builder.mutation<void, string>({
      query: (id) => ({
        url: `/api/stations/${encodeURIComponent(id)}`,
        method: 'DELETE',
      }),
      transformResponse: () => undefined,
      invalidatesTags: [{ type: 'Station', id: 'LIST' }],
    }),

    getNetcdfJobs: builder.query<
      OpsPaginated<Record<string, unknown>>,
      NetcdfJobsListParams | void
    >({
      query: (arg) => {
        const p = arg || {};
        return {
          url: '/api/netcdf/jobs',
          params: {
            pageno: p.pageno ?? 1,
            pagesize: p.pagesize ?? 20,
            ...(p.q ? { q: p.q } : {}),
          },
        };
      },
      transformResponse: (raw: unknown) => unwrapOpsPaginated<Record<string, unknown>>(raw),
      providesTags: [{ type: 'NetcdfJob', id: 'LIST' }],
    }),
    getNetcdfJobById: builder.query<Record<string, unknown>, string>({
      query: (id) => ({ url: `/api/netcdf/jobs/${encodeURIComponent(id)}` }),
      transformResponse: (raw: unknown) => unwrapOpsObject(raw) ?? {},
      providesTags: (_r, _e, id) => [{ type: 'NetcdfJob', id }],
    }),
    createNetcdfJob: builder.mutation<Record<string, unknown>, CreateNetcdfJobBody>({
      query: (body) => ({ url: '/api/netcdf/jobs', method: 'POST', data: body }),
      transformResponse: (raw: unknown) => unwrapOpsObject(raw) ?? {},
      invalidatesTags: [{ type: 'NetcdfJob', id: 'LIST' }],
    }),
    patchNetcdfJobStatus: builder.mutation<
      Record<string, unknown>,
      { id: string; body: Record<string, unknown>; internalKey?: string }
    >({
      query: ({ id, body, internalKey }) => ({
        url: `/api/netcdf/jobs/${encodeURIComponent(id)}/status`,
        method: 'PATCH',
        data: body,
        headers: internalKey ? { 'x-internal-key': internalKey } : undefined,
      }),
      transformResponse: (raw: unknown) => unwrapOpsObject(raw) ?? {},
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'NetcdfJob', id: 'LIST' },
        { type: 'NetcdfJob', id },
      ],
    }),

    /**
     * GET /api/roles — Danh sách roles
     */
    getRoles: builder.query<Record<string, unknown>[], void>({
      query: () => ({ url: '/api/roles' }),
      transformResponse: (raw: unknown) => unwrapOpsList(raw),
      providesTags: [
        { type: 'Auth' as const, id: 'ROLES-LIST' },
        { type: 'AdminMenu' as const, id: 'ROLES' },
      ],
    }),
    /**
     * POST /api/roles — Tạo role mới (`code` theo gateway; không dùng `slug`).
     */
    createRole: builder.mutation<
      Record<string, unknown>,
      { code: string; name: string; description?: string }
    >({
      query: (body) => ({ url: '/api/roles', method: 'POST', data: body }),
      transformResponse: (raw: unknown) => unwrapOpsObject(raw) ?? {},
      invalidatesTags: [
        { type: 'Auth' as const, id: 'ROLES-LIST' },
        { type: 'AdminMenu' as const, id: 'ROLES' },
      ],
    }),
    /**
     * GET /api/roles/{id} — Chi tiết role theo ID
     */
    getRoleById: builder.query<Record<string, unknown>, string>({
      query: (id) => ({ url: `/api/roles/${id}` }),
      transformResponse: (raw: unknown) => unwrapOpsObject(raw) ?? {},
      providesTags: (_r, _e, id) => [{ type: 'Auth' as const, id: `ROLE-${id}` }],
    }),
    /**
     * PATCH /api/roles/{id} — Cập nhật role
     */
    patchRole: builder.mutation<
      Record<string, unknown>,
      { id: string; body: { slug?: string; name?: string; description?: string } }
    >({
      query: ({ id, body }) => ({ url: `/api/roles/${id}`, method: 'PATCH', data: body }),
      transformResponse: (raw: unknown) => unwrapOpsObject(raw) ?? {},
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Auth' as const, id: 'ROLES-LIST' },
        { type: 'AdminMenu' as const, id: 'ROLES' },
        { type: 'Auth' as const, id: `ROLE-${id}` },
      ],
    }),
    /**
     * DELETE /api/roles/{id} — Xóa role
     */
    deleteRole: builder.mutation<{ deleted: boolean }, string>({
      query: (id) => ({ url: `/api/roles/${id}`, method: 'DELETE' }),
      transformResponse: (raw: unknown) => {
        const result = unwrapOpsObject(raw) as { deleted?: boolean };
        return { deleted: result?.deleted ?? true };
      },
      invalidatesTags: [
        { type: 'Auth' as const, id: 'ROLES-LIST' },
        { type: 'AdminMenu' as const, id: 'ROLES' },
      ],
    }),

    getDataArtifacts: builder.query<Record<string, unknown>[], OpsListLimit | void>({
      query: (arg) => ({
        url: '/api/data/artifacts',
        params: arg && typeof arg === 'object' && arg.limit ? { limit: arg.limit } : undefined,
      }),
      transformResponse: (raw: unknown) => unwrapOpsList<Record<string, unknown>>(raw),
      providesTags: [{ type: 'DataArtifact', id: 'LIST' }],
    }),
    registerDataArtifact: builder.mutation<Record<string, unknown>, Record<string, unknown>>({
      query: (body) => ({ url: '/api/data/artifacts', method: 'POST', data: body }),
      transformResponse: (raw: unknown) => unwrapOpsObject(raw) ?? {},
      invalidatesTags: [{ type: 'DataArtifact', id: 'LIST' }],
    }),
    presignDataArtifact: builder.mutation<
      Record<string, unknown>,
      { id: string; body?: PresignArtifactBody }
    >({
      query: ({ id, body }) => ({
        url: `/api/data/artifacts/${encodeURIComponent(id)}/presign`,
        method: 'POST',
        data: body ?? { expires_sec: 900 },
      }),
      transformResponse: (raw: unknown) => unwrapOpsObject(raw) ?? {},
    }),

    getEtlJobs: builder.query<Record<string, unknown>[], OpsListLimit | void>({
      query: (arg) => ({
        url: '/api/etl-jobs',
        params: arg && typeof arg === 'object' && arg.limit ? { limit: arg.limit } : undefined,
      }),
      transformResponse: (raw: unknown) => unwrapOpsList<Record<string, unknown>>(raw),
      providesTags: [{ type: 'EtlJob', id: 'LIST' }],
    }),
    getEtlJobById: builder.query<Record<string, unknown>, string>({
      query: (id) => ({ url: `/api/etl-jobs/${encodeURIComponent(id)}` }),
      transformResponse: (raw: unknown) => unwrapOpsObject(raw) ?? {},
      providesTags: (_r, _e, id) => [{ type: 'EtlJob', id }],
    }),
    getEtlJobErrors: builder.query<Record<string, unknown>[], { id: string; limit?: string }>({
      query: ({ id, limit }) => ({
        url: `/api/etl-jobs/${encodeURIComponent(id)}/errors`,
        params: limit ? { limit } : undefined,
      }),
      transformResponse: (raw: unknown) => unwrapOpsList<Record<string, unknown>>(raw),
      providesTags: (_r, _e, { id }) => [{ type: 'EtlJob', id: `${id}-errors` }],
    }),
    createEtlJob: builder.mutation<Record<string, unknown>, Record<string, unknown>>({
      query: (body) => ({ url: '/api/etl-jobs', method: 'POST', data: body }),
      transformResponse: (raw: unknown) => unwrapOpsObject(raw) ?? {},
      invalidatesTags: [{ type: 'EtlJob', id: 'LIST' }],
    }),
    cancelEtlJob: builder.mutation<Record<string, unknown>, string>({
      query: (id) => ({
        url: `/api/etl-jobs/${encodeURIComponent(id)}/cancel`,
        method: 'PATCH',
      }),
      transformResponse: (raw: unknown) => unwrapOpsObject(raw) ?? {},
      invalidatesTags: (_r, _e, id) => [
        { type: 'EtlJob', id: 'LIST' },
        { type: 'EtlJob', id },
      ],
    }),
    patchEtlJobStatus: builder.mutation<
      Record<string, unknown>,
      { id: string; body: Record<string, unknown>; internalKey?: string }
    >({
      query: ({ id, body, internalKey }) => ({
        url: `/api/etl-jobs/${encodeURIComponent(id)}/status`,
        method: 'PATCH',
        data: body,
        headers: internalKey ? { 'x-internal-key': internalKey } : undefined,
      }),
      transformResponse: (raw: unknown) => unwrapOpsObject(raw) ?? {},
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'EtlJob', id: 'LIST' },
        { type: 'EtlJob', id },
      ],
    }),
  }),
});

export const {
  useGetServiceRootQuery,
  useLazyGetMetricsTextQuery,
  useGetDashboardSummaryQuery,
  useGetIotExportCsvQuery,
  useLazyGetIotExportCsvQuery,
  useGetSettingsMapQuery,
  useGetSettingByKeyQuery,
  usePutSettingMutation,
  usePostSettingsBulkMutation,
  useGetSensorTypesQuery,
  useGetSensorTypesPaginatedQuery,
  useCreateSensorTypeMutation,
  usePatchSensorTypeMutation,
  useDeleteSensorTypeMutation,
  useGetAlertThresholdsQuery,
  useGetAlertThresholdByIdQuery,
  usePutStationThresholdsBulkMutation,
  usePatchAlertThresholdRuleMutation,
  useGetAlertHistoryQuery,
  usePatchAlertAckMutation,
  useGetStationsAggregateQuery,
  useGetAuditLogsQuery,
  useGetAdminUsersQuery,
  useGetAdminUserByIdQuery,
  useLazyGetAdminUserByIdQuery,
  useCreateAdminUserMutation,
  usePatchAdminUserMutation,
  useDeleteAdminUserMutation,
  usePutAdminUserPasswordMutation,
  usePutAdminUserRoleMutation,
  usePutAdminUserActiveMutation,
  useLazyGetUsersImportTemplateQuery,
  useImportUsersExcelMutation,
  useGetMenusAdminTreeQuery,
  useGetMenuRolesQuery,
  useGetMenuByIdQuery,
  useGetMenuRolesForMenuQuery,
  useCreateMenuMutation,
  usePatchMenuMutation,
  useDeleteMenuMutation,
  usePutMenuRolesMutation,
  useGetMapLayersPublishedQuery,
  useGetMapLayersQuery,
  useCreateMapLayerMutation,
  usePatchMapLayerPublishMutation,
  usePatchMapLayerStyleMutation,
  usePatchMapLayerAclMutation,
  useGetMapInfrastructureQuery,
  useGetProvincesListQuery,
  useGetWardsListQuery,
  useGetBasinsListQuery,
  useGetCatalogRootQuery,
  useGetCatalogRowsQuery,
  useGetCatalogRowQuery,
  useCreateCatalogRowMutation,
  usePutCatalogRowMutation,
  useDeleteCatalogRowMutation,
  useGetBasinsQuery,
  useGetBasinByIdQuery,
  useCreateBasinMutation,
  usePatchBasinMutation,
  useDeleteBasinMutation,
  useGetStationsQuery,
  useGetStationLinkProtocolsQuery,
  useGetStationByIdQuery,
  useGetStationWaterLevelsQuery,
  useGetStationTelemetrySeriesQuery,
  useCreateStationMutation,
  usePatchStationMutation,
  useDeleteStationMutation,
  useGetNetcdfJobsQuery,
  useGetNetcdfJobByIdQuery,
  useCreateNetcdfJobMutation,
  usePatchNetcdfJobStatusMutation,
  useGetDataArtifactsQuery,
  useRegisterDataArtifactMutation,
  usePresignDataArtifactMutation,
  useGetEtlJobsQuery,
  useGetEtlJobByIdQuery,
  useGetEtlJobErrorsQuery,
  useCreateEtlJobMutation,
  useCancelEtlJobMutation,
  usePatchEtlJobStatusMutation,
  // Roles
  useGetRolesQuery,
  useCreateRoleMutation,
  useGetRoleByIdQuery,
  usePatchRoleMutation,
  useDeleteRoleMutation,
} = opsManagementApi;
